import {
    useEffect, useMemo, useRef, useState,
} from 'react';
import {
    Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, FormLabel, IconButton, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { App } from 'BMapsSrc/BMapsApp';
import { molTypeToString } from 'BMapsSrc/ui/ui_utils';
import { DialogContentWithTooltip } from 'BMapsUI/common/DialogContentSection';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import Select from 'BMapsUI/common/Select';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import useCompoundUpdateSubscription from 'BMapsSrc/ui/common/helpers/useCompoundUpdateSubscription';
import { Loader } from 'BMapsSrc/Loader';
import { MolProps } from 'BMapsModel';
import { userConfirmation } from 'BMapsSrc/utils';
import {
    ABFE_PEPTIDE_HEAVY_ATOMS, FEP_CALCULATIONS, FEP_ENGINES, FEP_PROGRAMS,
    GROMACS_MAPPINGS,
    GROMACS_NETWORK_LAYOUTS,
    GROMACS_RBFE_MODES,
    TMD_NETWORK_POLICIES, bindingChain, bindingSiteResidueSpecs, estimatedEdges, fepDefaultTarget,
    fepDgShortfall, fepFormDefaults, fepTargets, largeAbfeLigands, openfeModes, parseResidueSpecs,
    abfeDefaultWindows, abfeWindowsCustom, abfeWindowsProblem, peptideAvailable,
    peptideGpuHours, receptorGroupsOnChains, resolveFepProgram, startFepScoring,
} from 'BMapsSrc/integrations/fep';
import { BasicTextField } from '../../SimulationPane/Components';
import { ImportErrors } from '../../ImportPane/import_components';
import { openFreeEnergySidePanel } from '../FreeEnergySidePanel';

const DEFAULT_RUNTIME_HOURS = 48;

// BACKLOG: TMD extra-sampling residues. The request/cpaws plumbing is in
// place, but the tmd driver on ConiferGPU2 defines its REST region only by
// ligand SMARTS and has no protein-residue option yet, so the field would
// always produce a failed edge. Flip this once the driver accepts
// --rest_residues.
const TMD_RESIDUE_SAMPLING_ENABLED = false;

/**
 * GROMACS RBFE: about how long the campaign takes on ConiferGPU2, in hours
 * (edges run one at a time there).
 * @param {number} n - ligand count
 * @param {{ layout?: string, mode?: string }} [opts]
 * @returns {number}
 */
export function gromacsCampaignHours(n, { layout='mst', mode='production' }={}) {
    const perEdge = (GROMACS_RBFE_MODES[mode] || GROMACS_RBFE_MODES.production).hoursPerEdge;
    return estimatedEdges(n, layout) * perEdge;
}

/**
 * Human-readable size of the campaign, for the form and the confirmation.
 * @param {{ calculation: string, engine: string }} spec
 * @param {number} n - ligand count
 * @param {string} policy - TMD network policy
 * @param {{ layout?: string, mode?: string }} [gromacs] - GROMACS layout and sampling
 * @returns {string}
 */
export function describeCampaign(spec, n, policy, gromacs={}) {
    if (!spec) return '';
    if (spec.calculation === 'abfe') {
        return `${n} absolute calculation${n === 1 ? '' : 's'} (one per ligand)`;
    }
    if (spec.engine === 'gromacs') {
        const layout = gromacs.layout || 'mst';
        const edges = estimatedEdges(n, layout);
        const hours = gromacsCampaignHours(n, gromacs);
        const about = layout === 'redundant' ? 'about ' : '';
        return `${n} ligands; OpenFE's planner picks ${about}${edges} edge${edges === 1 ? '' : 's'} `
            + `(${layout}), about ${hours.toFixed(hours < 10 ? 1 : 0)} h on ConiferGPU2 `
            + '(one edge at a time)';
    }
    if (spec.engine === 'tmd') {
        const edges = policy === 'complete' ? (n * (n - 1)) / 2 : Math.max(n - 1, 0);
        return `${n} ligands, ${edges} edge${edges === 1 ? '' : 's'} (${policy} network)`;
    }
    return `${n} ligands; OpenFE plans the network (at least ${Math.max(n - 1, 0)} edges)`;
}

/**
 * How a ligand is listed in the form: its resSpec (unique within a structure,
 * and the name the results come back under), with the display name when that
 * says more. Docked poses of one molecule are separate ligands here, so this
 * deliberately does not collapse by structure the way the compound table does.
 * @param {import('BMapsModel').Compound} c
 * @returns {string}
 */
export function ligandLabel(c) {
    const name = c.displayName || (typeof c.getName === 'function' ? c.getName() : null);
    return name && name !== c.resSpec ? `${c.resSpec} (${name})` : c.resSpec;
}

/** A labelled column of checkboxes, as the simulation form lays out chains. */
export function CheckboxGroup({
    label, items, selectedKeys, getKey, getName, onToggle, helperText, disabled,
}) {
    return (
        <Box sx={{ minWidth: '12rem' }}>
            <FormGroup>
                <FormLabel sx={{ fontSize: '0.85rem' }}>{`${label}:`}</FormLabel>
                {items.map((item) => {
                    const key = getKey(item);
                    return (
                        <FormControlLabel
                            key={key}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={selectedKeys.includes(key)}
                                    onChange={(e) => onToggle(key, e.target.checked)}
                                    disabled={disabled}
                                />
                            )}
                            label={getName(item)}
                        />
                    );
                })}
            </FormGroup>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
    );
}

/** "Chain B (peptide)": the kind matters for a chain that is not plain protein. */
export function chainLabel(chainGroup) {
    const kind = chainGroup.molType ? molTypeToString(chainGroup.molType) : '';
    const plain = !kind || /^(ref_)?protein$/i.test(String(chainGroup.molType));
    return plain ? `Chain ${chainGroup.chain}` : `Chain ${chainGroup.chain} (${kind})`;
}

export default function NewFreeEnergyJob({
    initData, selected, setHighlightedJobId,
}) {
    const workspaceCompounds = useCompoundUpdateSubscription();
    const selectedCompounds = useMemo(() => initData?.selectedCompounds || [], [initData]);
    const count = selectedCompounds.length;

    // The form's defaults, with anything Gibbs pre-filled applied on top and
    // validated against the real option lists (`fepFormDefaults`). Computed
    // once as the useState seeds, so the user owns every knob from then on —
    // a later initData change must not yank a control out from under them.
    const prefill = useMemo(() => fepFormDefaults(initData, count), [initData, count]);

    const [engine, setEngine] = useState(prefill.engine);
    const [calculation, setCalculation] = useState(prefill.calculation);
    // A target Gibbs pre-filled is kept; otherwise ConiferGPU2 is the default on dev and lab
    // builds (it is in-house only) and production keeps the fleet.
    const [target, setTarget] = useState(() => {
        if (initData?.target) return prefill.target;
        return Loader.AllowLabFeatures ? 'conifergpu2' : 'cpaws';
    });
    const [mode, setMode] = useState(prefill.mode);
    const [runOptimization, setRunOptimization] = useState(prefill.runOptimization);
    const [networkPolicy, setNetworkPolicy] = useState(prefill.networkPolicy);
    const [reference, setReference] = useState('');
    const [layout, setLayout] = useState('mst');
    const [mapping, setMapping] = useState('pmx');
    // ABFE lambda windows per leg: the sampling mode's defaults until edited.
    const [solventWindows, setSolventWindows] = useState(
        String(abfeDefaultWindows('production').solvent),
    );
    const [complexWindows, setComplexWindows] = useState(
        String(abfeDefaultWindows('production').complex),
    );
    const [windowsEdited, setWindowsEdited] = useState(false);
    const [central, setCentral] = useState('');
    const [residuesText, setResiduesText] = useState('');
    // Receptor selection: chains, ions, cofactors of the ligands' structure.
    const [chainIds, setChainIds] = useState([]);
    // Off: BMaps' protonation is kept and only chain ends / disulfides are
    // repaired for OpenMM. On: PDBFixer re-places every hydrogen at pH 7.
    const [reprotonate, setReprotonate] = useState(false);
    const [ionSpecs, setIonSpecs] = useState([]);
    const [cofactorSpecs, setCofactorSpecs] = useState([]);
    const [runtimeHours, setRuntimeHours] = useState(String(DEFAULT_RUNTIME_HOURS));
    const [jobName, setJobName] = useState(prefill.jobName);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitErrors, setSubmitErrors] = useState();

    // Re-seed when Gibbs explicitly opens the panel AGAIN with different
    // choices. The useState seeds above intentionally run once, so a chemist
    // mid-edit is never overwritten -- but that left a second handoff showing
    // the FIRST request's values while the chat reported the second's. Keyed on
    // `prefillNonce`, which `UICmds.openFreeEnergyPanel` stamps per explicit
    // open: an incidental initData change (selectedCompounds updating as the
    // user ticks ligands, for instance) carries the same nonce and is ignored,
    // so this re-applies on a real new request and nothing else.
    const prefillNonce = initData?.prefillNonce;
    const seededNonce = useRef(prefillNonce);
    useEffect(() => {
        if (prefillNonce == null || prefillNonce === seededNonce.current) return;
        seededNonce.current = prefillNonce;
        setEngine(prefill.engine);
        setCalculation(prefill.calculation);
        setTarget(prefill.target);
        setMode(prefill.mode);
        setRunOptimization(prefill.runOptimization);
        setNetworkPolicy(prefill.networkPolicy);
        setJobName(prefill.jobName);
    }, [prefillNonce, prefill]);

    // A single compound is always absolute; TMD is relative-only.
    const effectiveCalculation = count === 1 ? 'abfe' : calculation;
    const effectiveEngine = effectiveCalculation === 'abfe' ? 'openfe' : engine;
    const resolved = resolveFepProgram({
        count, engine: effectiveEngine, calculation: effectiveCalculation,
    });
    const spec = resolved.program ? FEP_PROGRAMS[resolved.program] : null;
    const isTmd = spec?.engine === 'tmd';
    const isOpenfe = spec?.engine === 'openfe';
    const isGromacs = spec?.engine === 'gromacs';
    const isAbfe = spec?.calculation === 'abfe';
    const targets = spec ? fepTargets(spec.key) : [];
    const largeLigands = useMemo(
        () => largeAbfeLigands(
            selectedCompounds, (c) => Number(c.getMolProp?.(MolProps.HeavyAtoms)) || 0,
        ),
        [selectedCompounds],
    );
    const windowsProblem = isAbfe ? abfeWindowsProblem(solventWindows, complexWindows) : null;
    const defaultWindows = abfeDefaultWindows(mode);
    // Another sampling mode brings its own default windows, unless the user chose some.
    useEffect(() => {
        if (windowsEdited) return;
        setSolventWindows(String(defaultWindows.solvent));
        setComplexWindows(String(defaultWindows.complex));
    }, [mode, windowsEdited, defaultWindows.solvent, defaultWindows.complex]);
    // The peptide profile exists for absolute calculations on targets that can plan it.
    useEffect(() => {
        if (!isAbfe || !peptideAvailable(target)) {
            setMode((m) => (m === 'peptide' ? 'production' : m));
        }
    }, [isAbfe, target]);
    // A program change must not leave a target selected that cannot run it.
    useEffect(() => {
        if (spec) setTarget((t) => fepDefaultTarget(spec.key, t));
    }, [spec]);

    // Workspace compounds that are not ligands of this job: candidates to add
    // as ligands.
    const otherCompounds = useMemo(
        () => (workspaceCompounds || []).filter((c) => !selectedCompounds.includes(c)),
        [workspaceCompounds, selectedCompounds]
    );
    // The receptor is the ligands' structure: its protein chains, and the
    // ions and cofactors (HEM, NAD, ATP, ... — the residue names in
    // `cofactorResidueNames`) that came in with it. Nothing from another
    // structure or from the workspace's compounds.
    const { caseData, mapCase } = useMemo(
        () => (selectedCompounds.length > 0 && App.Ready
            ? (App.getDataParents(selectedCompounds[0]) || {}) : {}),
        [selectedCompounds],
    );
    // Every chain of the structure: protein chains AND the polymer / peptide
    // chains BMaps classifies separately (4YZU's 57-residue light chain is a
    // polymer; listing protein chains only silently dropped it from the
    // receptor). Their residues all export the same way.
    const proteinChains = useMemo(() => {
        const chains = [
            ...(caseData?.getProteinChains?.() || []),
            ...(caseData?.getPolymers?.() || []),
        ];
        return [...chains].sort((a, b) => String(a.chain).localeCompare(String(b.chain)));
    }, [caseData]);
    const allChainIds = useMemo(() => proteinChains.map((c) => c.chain), [proteinChains]);
    const allIons = useMemo(() => caseData?.getIons?.() || [], [caseData]);
    const allCofactors = useMemo(() => caseData?.getCofactors?.() || [], [caseData]);
    // Offered: on a selected chain, or on no protein chain at all (the
    // simulation form's rule); the defaults select everything offered.
    const ionChoices = receptorGroupsOnChains(allIons, chainIds, allChainIds);
    const cofactorChoices = receptorGroupsOnChains(allCofactors, chainIds, allChainIds);

    useEffect(() => {
        // A new structure (or a changed chain selection) resets the receptor
        // extras to their defaults: every chain, every offered ion / cofactor.
        setChainIds(allChainIds);
    }, [mapCase, allChainIds]);
    useEffect(() => {
        setIonSpecs(receptorGroupsOnChains(allIons, chainIds, allChainIds).map((g) => g.resSpec));
        setCofactorSpecs(
            receptorGroupsOnChains(allCofactors, chainIds, allChainIds).map((g) => g.resSpec),
        );
    }, [mapCase, chainIds, allIons, allCofactors, allChainIds]);

    // GROMACS RBFE takes one chain: default to the one the ligands bind.
    useEffect(() => {
        if (!spec?.singleChain || allChainIds.length < 2) return;
        let chain;
        try {
            chain = bindingChain(selectedCompounds);
        } catch (ex) {
            chain = undefined;
        }
        setChainIds((prev) => (prev.length === 1 ? prev : [chain || allChainIds[0]]));
    }, [spec, allChainIds, selectedCompounds]);

    const ions = ionChoices.filter((g) => ionSpecs.includes(g.resSpec));
    const cofactors = cofactorChoices.filter((g) => cofactorSpecs.includes(g.resSpec));
    const gromacsHours = isGromacs ? gromacsCampaignHours(count, { layout, mode }) : 0;
    const showReceptor = count > 0;
    // Too few compounds for a relative run: shown in the form and blocks Submit.
    const shortfall = count > 0 && spec ? fepDgShortfall(count, spec.key) : null;

    // The selection is owned by the panel props (like the screening form), so
    // changing it re-opens the tab with the new list.
    function setSelection(compounds) {
        openFreeEnergySidePanel('new_fep_tab', { ...initData, selectedCompounds: compounds });
    }

    function toggleIn(setter) {
        return (key, on) => setter((prev) => (on
            ? [...prev.filter((k) => k !== key), key]
            : prev.filter((k) => k !== key)));
    }

    function disableSubmitMessage() {
        if (count < 1) return 'Select at least one compound to submit';
        if (resolved.error) return resolved.error;
        if (shortfall) return shortfall;
        if (proteinChains.length > 0 && chainIds.length === 0) {
            return 'Select at least one receptor chain';
        }
        if (spec?.singleChain && chainIds.length > 1) {
            return `${spec.label} takes one receptor chain: select the chain the ligands bind`;
        }
        if (!targets.some((t) => t.key === target && !t.disabled)) {
            return 'Choose a compute target that runs this program';
        }
        if (windowsProblem) return windowsProblem;
        if (!Number.isFinite(Number(runtimeHours)) || Number(runtimeHours) < 0) {
            return 'Runtime cap must be a number of hours (0 for the worker default)';
        }
        return false;
    }

    function editWindows(setter, value) {
        setter(value);
        setWindowsEdited(true);
    }

    function useBindingSiteResidues() {
        const specs = bindingSiteResidueSpecs(selectedCompounds);
        setResiduesText(specs.join(', '));
    }

    async function submit() {
        const targetLabel = targets.find((t) => t.key === target)?.label || target;
        const lines = [
            `${spec.label}: ${describeCampaign(spec, count, networkPolicy, { layout, mode })}.`,
            `Compute: ${targetLabel}.`,
        ];
        const spend = target === 'cpaws' ? ' and spends real money on the fleet' : '';
        lines.push(`This runs for hours on GPUs${spend}. Watch it in the Jobs tab.`);
        const ok = await userConfirmation(`Submit ${spec.label}?`, lines.join('\n\n'));
        if (!ok) return;

        setIsSubmitting(true);
        setSubmitErrors(undefined);
        try {
            const hours = Number(runtimeHours);
            const { results, error } = await startFepScoring(selectedCompounds, {
                program: spec.key,
                target,
                mode,
                abfeWindows: isAbfe
                    ? { solvent: solventWindows, complex: complexWindows } : undefined,
                runOptimization,
                network: (isTmd && { policy: networkPolicy, reference: reference || undefined })
                    || (isGromacs && { layout, mapping, central: central || undefined })
                    || undefined,
                extraSamplingResidues: (isTmd && TMD_RESIDUE_SAMPLING_ENABLED)
                    ? parseResidueSpecs(residuesText) : undefined,
                cofactors: (isTmd || isGromacs) ? [] : cofactors,
                chains: proteinChains.length > 0 ? chainIds : undefined,
                ions: isGromacs ? [] : ions,
                receptorPrep: reprotonate ? 'full' : 'termini',
                maxRuntimeMinutes: hours > 0 ? Math.round(hours * 60) : undefined,
                jobName: jobName || undefined,
            });
            if (error) {
                setSubmitErrors([error]);
                return;
            }
            if (!results?.jobid) {
                setSubmitErrors(['No job id returned from the job manager.']);
                return;
            }
            setJobName('');
            setHighlightedJobId(results.jobid);
            openFreeEnergySidePanel('fep_jobs_tab', { ...initData, selectedCompounds: [] });
        } catch (ex) {
            setSubmitErrors([`There was an error submitting the free-energy job. ${ex}`]);
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!selected) return null;

    let campaignNote = '';
    if (count === 1) {
        campaignNote = 'A single compound runs as an absolute binding free energy (OpenFE ABFE).';
    } else if (spec) {
        campaignNote = describeCampaign(spec, count, networkPolicy, { layout, mode });
    }
    const runtimeCap = Number(runtimeHours);
    const capWarning = isGromacs && runtimeCap > 0 && gromacsHours > runtimeCap
        ? `The runtime cap (${runtimeCap} h) is shorter than the estimate (about ${Math.round(gromacsHours)} h): the edges left when it is reached will not run. Raise it, or use 0 for the worker's cap (6 days).`
        : null;

    return (
        <>
            <DialogContentWithTooltip
                title={count > 0 ? `Ligands (${count} selected)` : 'Ligands'}
                disablePadding
                style={{ width: '100%' }}
                ContainerProps={{ sx: { overflow: 'unset !important' } }}
                collapsible
                defaultExpand
            >
                {count > 0 ? (
                    <Box component="ul" sx={{ listStyle: 'none', margin: '0.25rem 0.5rem 0', padding: 0 }}>
                        {selectedCompounds.map((c) => (
                            <Box
                                component="li"
                                key={c.resSpec}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.1rem 0',
                                }}
                            >
                                <span style={{ flex: 1, overflowWrap: 'anywhere' }}>{ligandLabel(c)}</span>
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${c.resSpec} from the ligands`}
                                    title="Remove from the ligands"
                                    onClick={() => setSelection(
                                        selectedCompounds.filter((x) => x !== c),
                                    )}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ margin: '0.5rem' }}>
                        {workspaceCompounds?.length > 0
                            ? 'Select compounds in the workspace, or add them here.'
                            : 'Load or build compounds to run free-energy calculations.'}
                    </Box>
                )}
                {otherCompounds.length > 0 && (
                    <Select
                        id="fep-add-ligand"
                        label="Add a ligand"
                        value=""
                        onChange={(e) => {
                            const c = otherCompounds.find((x) => x.resSpec === e.target.value);
                            if (c) setSelection([...selectedCompounds, c]);
                        }}
                        sx={{ width: '100%', marginTop: '0.75rem' }}
                    >
                        <option value="">Choose a workspace compound…</option>
                        {otherCompounds.map((c) => (
                            <option key={c.resSpec} value={c.resSpec}>{ligandLabel(c)}</option>
                        ))}
                    </Select>
                )}
                <Box sx={{ margin: '0.5rem', fontStyle: 'italic' }}>
                    {campaignNote}
                </Box>
                {shortfall && (
                    <Box sx={{ margin: '0.5rem', color: 'var(--marketing-orange, #b8650c)' }}>
                        {shortfall}
                    </Box>
                )}
            </DialogContentWithTooltip>

            {showReceptor && (
                <DialogContentWithTooltip
                    title="Receptor"
                    ComponentProps={{ sx: { width: '100%' } }}
                    ContainerProps={{ sx: { overflow: 'unset !important' } }}
                    collapsible
                    defaultExpand
                >
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: '1rem 2rem', marginTop: '0.5rem',
                    }}
                    >
                        {proteinChains.length > 1 && (
                            <CheckboxGroup
                                label="Target chains"
                                items={proteinChains}
                                selectedKeys={chainIds}
                                getKey={(c) => c.chain}
                                getName={chainLabel}
                                onToggle={toggleIn(setChainIds)}
                                helperText={spec?.singleChain
                                    ? `${spec.label} takes one chain: the one the ligands bind.`
                                    : 'Only these chains go into the receptor.'}
                            />
                        )}
                        {ionChoices.length > 0 && (
                            <CheckboxGroup
                                label="Ions"
                                items={ionChoices}
                                selectedKeys={ionSpecs}
                                getKey={(g) => g.resSpec}
                                getName={(g) => g.displayName || g.resSpec}
                                onToggle={toggleIn(setIonSpecs)}
                                disabled={isGromacs}
                                helperText={isGromacs
                                    ? 'GROMACS RBFE simulates the protein only; ions are left out.'
                                    : 'Kept in the receptor (structural ions). Unchecked ions are dropped.'}
                            />
                        )}
                        {cofactorChoices.length > 0 && (
                            <CheckboxGroup
                                label="Cofactors"
                                items={cofactorChoices}
                                selectedKeys={cofactorSpecs}
                                getKey={(g) => g.resSpec}
                                getName={(g) => g.displayName || g.resSpec}
                                onToggle={toggleIn(setCofactorSpecs)}
                                disabled={isTmd || isGromacs}
                                helperText={(isTmd || isGromacs)
                                    ? `${isTmd ? 'TMD' : 'GROMACS RBFE'} does not take cofactors; they are left out.`
                                    : 'Sent along with the receptor, not scored.'}
                            />
                        )}
                    </Box>
                    {proteinChains.length > 1 && chainIds.length === 0 && (
                        <Box sx={{ margin: '0.5rem 0 0', color: 'var(--marketing-orange, #b8650c)' }}>
                            Select at least one receptor chain.
                        </Box>
                    )}
                    {spec?.singleChain && chainIds.length > 1 && (
                        <Box sx={{ margin: '0.5rem 0 0', color: 'var(--marketing-orange, #b8650c)' }}>
                            {`${spec.label} takes one receptor chain: select only the chain the ligands bind.`}
                        </Box>
                    )}
                    <Box sx={{ marginTop: '0.75rem' }}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    id="fep-reprotonate"
                                    size="small"
                                    checked={reprotonate}
                                    onChange={(e) => setReprotonate(e.target.checked)}
                                />
                            )}
                            label="Re-protonate the receptor with PDBFixer (pH 7)"
                        />
                        <FormHelperText>
                            {reprotonate
                                ? 'Every hydrogen is re-placed at pH 7; protonation states follow PDBFixer, not BMaps.'
                                : 'BMaps\u2019 protonation is kept. Chain ends and disulfides are repaired so OpenMM can type the receptor.'}
                        </FormHelperText>
                    </Box>
                </DialogContentWithTooltip>
            )}

            <DialogContentWithTooltip
                title="Calculation"
                ComponentProps={{ sx: { width: '100%' } }}
                ContainerProps={{ sx: { overflow: 'unset !important' } }}
                collapsible
                defaultExpand
            >
                <Select
                    id="fep-calculation"
                    label="Calculation"
                    value={effectiveCalculation}
                    onChange={(e) => setCalculation(e.target.value)}
                    disabled={count === 1}
                    helperText={count === 1 ? 'One compound: absolute only' : undefined}
                    sx={{ width: '100%', marginTop: '0.5rem' }}
                >
                    {FEP_CALCULATIONS.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                </Select>
                <Select
                    id="fep-engine"
                    label="Engine"
                    value={effectiveEngine}
                    onChange={(e) => setEngine(e.target.value)}
                    disabled={effectiveCalculation === 'abfe'}
                    helperText={effectiveCalculation === 'abfe' ? 'Absolute runs use OpenFE' : undefined}
                    sx={{ width: '100%', marginTop: '1rem' }}
                >
                    {FEP_ENGINES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                </Select>
                <Select
                    id="fep-target"
                    label="Compute"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    sx={{ width: '100%', marginTop: '1rem' }}
                >
                    {targets.map((t) => (
                        <option key={t.key} value={t.key} disabled={t.disabled}>{t.label}</option>
                    ))}
                </Select>
                {isGromacs && (
                    <>
                        <Select
                            id="fep-gromacs-mode"
                            label="Sampling"
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            helperText={`production: the validated protocol, ${GROMACS_RBFE_MODES.production.repeats} repeats × ${GROMACS_RBFE_MODES.production.nsPerWindow} ns per λ window, about ${GROMACS_RBFE_MODES.production.hoursPerEdge} h per edge; fast: ${GROMACS_RBFE_MODES.fast.repeats} × ${GROMACS_RBFE_MODES.fast.nsPerWindow} ns, about ${GROMACS_RBFE_MODES.fast.hoursPerEdge} h per edge, not validated`}
                            sx={{ width: '100%', marginTop: '1rem' }}
                        >
                            <option value="production">Production</option>
                            <option value="fast">Fast</option>
                        </Select>
                        <Select
                            id="fep-gromacs-layout"
                            label="Network"
                            value={layout}
                            onChange={(e) => setLayout(e.target.value)}
                            helperText="OpenFE's planner picks the edges (Kartograf mapping, LOMAP scores); edges between ligands of different net charge are not run."
                            sx={{ width: '100%', marginTop: '1rem' }}
                        >
                            {GROMACS_NETWORK_LAYOUTS.map((l) => (
                                <option key={l.key} value={l.key}>{l.label}</option>
                            ))}
                        </Select>
                        <Select
                            id="fep-gromacs-mapping"
                            label="Atom mapping"
                            value={mapping}
                            onChange={(e) => setMapping(e.target.value)}
                            helperText="How each edge's atoms are matched. pmx is validated; Kartograf also maps more hydrogens and fixed one of two edges compared, but is not yet validated."
                            sx={{ width: '100%', marginTop: '1rem' }}
                        >
                            {GROMACS_MAPPINGS.map((m) => (
                                <option key={m.key} value={m.key}>{m.label}</option>
                            ))}
                        </Select>
                        {layout === 'radial' && (
                            <Select
                                id="fep-gromacs-central"
                                label="Central ligand"
                                value={central}
                                onChange={(e) => setCentral(e.target.value)}
                                sx={{ width: '100%', marginTop: '1rem' }}
                            >
                                <option value="">First selected</option>
                                {selectedCompounds.map((c) => (
                                    <option key={c.resSpec} value={c.resSpec}>{c.resSpec}</option>
                                ))}
                            </Select>
                        )}
                    </>
                )}
                {isOpenfe && (
                    <Select
                        id="fep-mode"
                        label="Sampling"
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                        helperText={isAbfe
                            ? 'production: OpenFE defaults, ~30-45 GPU-hours per ligand; fast: ~5x less sampling, ~6-9 GPU-hours, larger error; peptide: for large flexible ligands, 45 solvent and 46 complex windows (about twice production, from pilots on a cyclic peptide)'
                            : 'production: benchmark settings; fast: ~4-5x cheaper, lower accuracy'}
                        sx={{ width: '100%', marginTop: '1rem' }}
                    >
                        {openfeModes(isAbfe, target).map((m) => (
                            <option key={m.key} value={m.key} disabled={m.disabled}>
                                {m.label}
                            </option>
                        ))}
                    </Select>
                )}
                {isAbfe && (
                    <>
                        <BasicTextField
                            id="fep-abfe-solvent-windows"
                            labelText="Solvent windows"
                            value={solventWindows}
                            onChange={(e) => editWindows(setSolventWindows, e.target.value)}
                            required={false}
                            helperText={`Lambda windows in the solvent leg (default ${defaultWindows.solvent}); more windows overlap better and cost more.`}
                            sx={{ width: '100%', marginTop: '1rem' }}
                        />
                        <BasicTextField
                            id="fep-abfe-complex-windows"
                            labelText="Complex windows"
                            value={complexWindows}
                            onChange={(e) => editWindows(setComplexWindows, e.target.value)}
                            required={false}
                            helperText={`Lambda windows in the complex leg (default ${defaultWindows.complex}${mode === 'peptide' ? ', not yet validated' : ''}).`}
                            sx={{ width: '100%', marginTop: '1rem' }}
                        />
                        {windowsProblem ? (
                            <FormHelperText error id="fep-abfe-windows-problem">{windowsProblem}</FormHelperText>
                        ) : (
                            <FormHelperText id="fep-abfe-windows-hours">
                                {`About ${peptideGpuHours(solventWindows, complexWindows, mode).join('-')} GPU-hours per ligand at these window counts.${abfeWindowsCustom(mode, { solvent: solventWindows, complex: complexWindows }) ? ' Other than the default windows, the sampling is not validated.' : ''}`}
                            </FormHelperText>
                        )}
                    </>
                )}
                {isAbfe && mode !== 'peptide' && largeLigands.length > 0 && (
                    <FormHelperText id="fep-large-ligand-note" sx={{ marginTop: '0.5rem' }}>
                        {`${largeLigands.join(', ')} ${largeLigands.length === 1 ? 'has' : 'have'} more than ${ABFE_PEPTIDE_HEAVY_ATOMS} heavy atoms. The default ABFE windows are tuned for ligands of 20-50 heavy atoms and are unlikely to overlap for a ligand this large (the MBAR analysis may not converge); the peptide profile is for such ligands.`}
                    </FormHelperText>
                )}
                {target === 'cpaws' && (
                    <Select
                        id="fep-run-optimization"
                        label="Fleet"
                        value={runOptimization}
                        onChange={(e) => setRunOptimization(e.target.value)}
                        helperText="cost: spot instances; time: on-demand"
                        sx={{ width: '100%', marginTop: '1rem' }}
                    >
                        <option value="cost">Optimize cost (spot)</option>
                        <option value="time">Optimize time (on-demand)</option>
                    </Select>
                )}
                {isTmd && (
                    <>
                        <Select
                            id="fep-network"
                            label="Network"
                            value={networkPolicy}
                            onChange={(e) => setNetworkPolicy(e.target.value)}
                            sx={{ width: '100%', marginTop: '1rem' }}
                        >
                            {TMD_NETWORK_POLICIES.map((p) => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </Select>
                        {networkPolicy === 'star' && (
                            <Select
                                id="fep-reference"
                                label="Reference ligand"
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                sx={{ width: '100%', marginTop: '1rem' }}
                            >
                                <option value="">First selected</option>
                                {selectedCompounds.map((c) => (
                                    <option key={c.resSpec} value={c.resSpec}>{c.resSpec}</option>
                                ))}
                            </Select>
                        )}
                        {TMD_RESIDUE_SAMPLING_ENABLED && (
                        <Box sx={{ marginTop: '1rem' }}>
                            <BasicTextField
                                id="fep-extra-sampling-residues"
                                labelText="Extra-sampling residues (TMD)"
                                value={residuesText}
                                onChange={(e) => setResiduesText(e.target.value)}
                                required={false}
                                placeholder="A:123, A:145"
                                helperText="Residues given extra sampling in the complex leg, as CHAIN:RESNUM"
                                sx={{ width: '100%' }}
                            />
                            <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                                component="button"
                                onClick={useBindingSiteResidues}
                                disabled={count < 1}
                                sx={{ color: 'var(--marketing-blue)', marginTop: '0.25rem' }}
                            >
                                Use binding-site residues of the selected ligands
                            </Link>
                        </Box>
                        )}
                    </>
                )}
                <Box sx={{ marginTop: '1rem' }}>
                    <BasicTextField
                        id="fep-runtime-hours"
                        labelText="Runtime cap (hours)"
                        value={runtimeHours}
                        onChange={(e) => setRuntimeHours(e.target.value)}
                        required={false}
                        helperText="May only lower the worker's cap; edges that finished before the cap are still reported"
                        sx={{ width: '100%' }}
                    />
                    {capWarning && (
                        <Box sx={{ margin: '0.25rem 0 0', color: 'var(--marketing-orange, #b8650c)' }}>
                            {capWarning}
                        </Box>
                    )}
                </Box>
                <Box sx={{ marginTop: '1rem' }}>
                    <BasicTextField
                        id="fep-job-name"
                        labelText="Job name"
                        value={jobName}
                        onChange={(e) => setJobName(e.target.value)}
                        required={false}
                        sx={{ width: '100%' }}
                    />
                </Box>
            </DialogContentWithTooltip>

            <BMapsTooltip title={disableSubmitMessage() || ''}>
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        onClick={submit}
                        disabled={!!disableSubmitMessage() || isSubmitting}
                        sx={{ width: '100%' }}
                    >
                        {isSubmitting ? 'Submitting…' : `Submit ${spec ? spec.label : 'free-energy job'}`}
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <span style={{ fontStyle: 'italic' }}>
                Free-energy calculations run as jobs outside your BMaps session.
                <br />
                Watch them in the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => openFreeEnergySidePanel('fep_jobs_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Jobs
                </Link>
                {' '}
                tab; results and the Apply-to-compounds action are in the Results tab.
            </span>
            {submitErrors && <ImportErrors errors={submitErrors} />}
        </>
    );
}
